import { useMemo, useState } from "react";
import { isNil, uniqBy } from "lodash";
import { nanoid } from "nanoid";
import type { FieldTypes } from "@circle-react-shared/ProfileFields/constants";
import { useFilterValueContext } from "../FilterValueContext";
import type { BaseFilter, ProfileFieldBaseFilter } from "../types";
import { createBaseFilter, isProfileFieldBaseFilter } from "../types";

export interface AddFilterArgs {
  key: string;
  profile_field_id?: number;
  profile_field_type?: FieldTypes;
}

interface UseDynamicFiltersArgs {
  defaultFilterNames: string[];
  persistentFilterNames?: string[];
}

export const useDynamicFilters = ({
  defaultFilterNames,
  persistentFilterNames = [],
}: UseDynamicFiltersArgs) => {
  const { filterParams, onAdd } = useFilterValueContext();

  const getInitialDisplayedFilters = () => {
    const initialDisplayedFilters: Array<BaseFilter | ProfileFieldBaseFilter> =
      [];
    const alreadyIncludedFilterNames = new Set<string>();

    const getDisplayFilterParam = (filterParam: BaseFilter) => {
      if (isProfileFieldBaseFilter(filterParam)) {
        return initialDisplayedFilters.push({
          id: filterParam.id,
          key: filterParam.key,
          profile_field_id: filterParam.profile_field_id,
          profile_field_type: filterParam.profile_field_type,
        });
      }
      initialDisplayedFilters.push({
        id: filterParam.id,
        key: filterParam.key,
      });
      return undefined;
    };

    defaultFilterNames.forEach((filterItemName: string) => {
      const includedFilterParam = filterParams.find(
        filterParam => filterParam.key === filterItemName,
      );
      if (includedFilterParam) {
        alreadyIncludedFilterNames.add(filterItemName);
        getDisplayFilterParam(includedFilterParam);
      } else {
        initialDisplayedFilters.push(createBaseFilter({ key: filterItemName }));
      }
    });

    filterParams.forEach(filterParam => {
      if (alreadyIncludedFilterNames.has(filterParam.key)) {
        alreadyIncludedFilterNames.delete(filterParam.key);
      } else {
        getDisplayFilterParam(filterParam);
      }
    });
    return initialDisplayedFilters;
  };
  const [displayedFilters, setDisplayedFilters] = useState<
    BaseFilter[] | ProfileFieldBaseFilter[]
  >(getInitialDisplayedFilters);

  const isClearFiltersShown = useMemo(
    () =>
      filterParams.some(filter => {
        if ("filter_type" in filter) {
          return !isNil(filter.filter_type);
        }
        return !isNil(filter.value);
      }),
    [filterParams],
  );

  const handleClearFilters = () => {
    /* This cannot add profile field filters by default. But that shouldn't be an issue now. */
    setDisplayedFilters([
      ...defaultFilterNames.map(name => createBaseFilter({ key: name })),
      ...persistentFilterNames
        .filter(name => !defaultFilterNames.includes(name))
        .map(name => createBaseFilter({ key: name })),
    ]);
  };

  const handleAddFilter = ({
    key,
    profile_field_id,
    profile_field_type,
  }: AddFilterArgs) => {
    const newFilterParam = {
      id: nanoid(),
      key,
      profile_field_id,
      profile_field_type,
      isInitialOpen: true,
    };
    onAdd?.(newFilterParam);
    setDisplayedFilters(prevState => [...prevState, newFilterParam]);
  };

  const handleRemoveFilter = (id: string) => {
    setDisplayedFilters(prevState => {
      const filteredFilters = prevState.filter(
        filter =>
          persistentFilterNames.includes(filter.key) ||
          defaultFilterNames.includes(filter.key) ||
          filter.id !== id,
      );
      return uniqBy(filteredFilters, "key");
    });
  };

  return {
    displayedFilters,
    isClearFiltersShown,
    onClearFilters: handleClearFilters,
    onAddFilter: handleAddFilter,
    onRemoveFilter: handleRemoveFilter,
  };
};
